type Child = Node | string | number | null | undefined | false;

export interface ElementOptions {
  class?: string;
  text?: string;
  html?: string;
  style?: string;
  attrs?: Record<string, string | number | boolean | undefined>;
  on?: Record<string, EventListener>;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElementOptions = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (options.class) node.className = options.class;
  if (options.text !== undefined) node.textContent = options.text;
  if (options.html !== undefined) node.innerHTML = options.html;
  if (options.style) node.setAttribute("style", options.style);
  for (const [key, value] of Object.entries(options.attrs ?? {})) {
    if (value === undefined || value === false) continue;
    node.setAttribute(key, String(value));
  }
  for (const [event, handler] of Object.entries(options.on ?? {})) {
    node.addEventListener(event, handler);
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(typeof child === "string" || typeof child === "number" ? String(child) : child);
  }
  return node;
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

/**
 * Provider icon with a graceful fallback: many provider logos are served by a
 * third-party host that blocks cross-origin loads, so the badge degrades to the
 * first letter of the provider name.
 */
export function logoImage(url: string | undefined, name: string): HTMLElement {
  const fallback = el("span", { class: "logo logo--letter", text: name.slice(0, 1).toUpperCase() });
  if (!url) return fallback;
  const image = el("img", {
    class: "logo",
    attrs: {
      src: url,
      alt: "",
      width: "20",
      height: "20",
      loading: "lazy",
      decoding: "async",
      referrerpolicy: "no-referrer",
    },
  });
  image.addEventListener("error", () => image.replaceWith(fallback));
  return image;
}

/** Append children, skipping empty values. */
export function appendChildren(parent: HTMLElement, ...children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.append(typeof child === "string" || typeof child === "number" ? String(child) : child);
  }
}

export function svg(markup: string): SVGElement {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = markup.trim();
  return wrapper.firstElementChild as SVGElement;
}

export function showToast(message: string): void {
  let toast = document.getElementById("toast") ?? document.querySelector<HTMLElement>(".toast");
  if (!toast) {
    toast = el("div", {
      class: "toast",
      attrs: { role: "status", "aria-live": "polite", "aria-atomic": "true" },
    });
    document.body.append(toast);
  }
  toast.textContent = message;
  toast.dataset.open = "true";
  window.clearTimeout(Number(toast.dataset.timer ?? "0"));
  toast.dataset.timer = String(
    window.setTimeout(() => {
      toast!.dataset.open = "false";
    }, 2200),
  );
}

/** Errors stay on screen until the reader dismisses them. */
export function showAlert(message: string): void {
  const region = document.getElementById("alerts");
  if (!region) return;
  const alert = el("div", { class: "toast toast--alert", attrs: { role: "alert" } });
  const dismiss = el("button", {
    class: "ghost-link alert-dismiss",
    attrs: { type: "button", "aria-label": "Dismiss error" },
    text: "Dismiss",
  });
  dismiss.addEventListener("click", () => alert.remove());
  alert.append(el("p", { class: "toast-text", text: message }), dismiss);
  region.append(alert);
  // An error about one page should not still be on screen three routes later.
  window.setTimeout(() => alert.remove(), 12000);
}

/** Leaving a page clears the errors that belonged to it. */
export function clearAlerts(): void {
  document.getElementById("alerts")?.replaceChildren();
}

export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const rest = whole % 60;
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}
